import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmCommentsCreateInput } from '../../../server/queries/crm-comments-create.ts';
import { api } from '../../../utils/api.ts';
import { RichTextForm } from '../../rich-text/components/rich-text-form.tsx';
import type { RichTextValue } from '../../rich-text/components/rich-text-input.tsx';
import { RichTextPlaceholder } from '../../rich-text/components/rich-text-placeholder.tsx';

type NewCommentProps = { taskId: string } | { noteId: string };

type State = {
    isAddingComment: boolean;
    isCommentWasAdded: boolean;
};

export const NewComment = (props: NewCommentProps) => {
    const { t } = useLocale('tasks');
    const { toast } = useToast();
    const containerRef = useRef<HTMLDivElement>(null);
    const [{ isAddingComment, isCommentWasAdded }, setState] = useState<State>({
        isAddingComment: false,
        isCommentWasAdded: false,
    });

    const apiUtils = api.useUtils();

    const createCommentMutation = api.platoUser.command.comments.create.useMutation({
        onSuccess: (newComment) => {
            // Optimistically update the cache by adding the new comment to the end of the list
            apiUtils.platoUser.query.comments.getManyComments.setData(props, (oldData) => {
                if (!oldData) {
                    return [newComment];
                }
                return [...oldData, newComment];
            });
            setState({ isAddingComment: false, isCommentWasAdded: true });
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    useEffect(() => {
        if (isCommentWasAdded || isAddingComment) {
            containerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isCommentWasAdded, isAddingComment]);

    const handleAddComment = (content: RichTextValue) => {
        const input: CrmCommentsCreateInput = {
            contentRich: content,
            ...props,
        };
        createCommentMutation.mutate(input);
    };

    return (
        <div ref={containerRef}>
            {isAddingComment ? (
                <RichTextForm
                    autoFocus
                    entityType="comment"
                    placeholder={t('comments__add_placeholder')}
                    submitTitle={t('comments__save')}
                    onSubmit={handleAddComment}
                    onCancel={() => {
                        setState({ isAddingComment: false, isCommentWasAdded: false });
                    }}
                    isSubmitting={createCommentMutation.isLoading}
                />
            ) : (
                <RichTextPlaceholder
                    onClick={() => {
                        setState({ isAddingComment: true, isCommentWasAdded: false });
                    }}
                    placeholderText={t('comments__add_placeholder')}
                />
            )}
        </div>
    );
};
